/**
 * Read a list out of an OCS response, whatever envelope it arrived in.
 *
 * WHY THIS EXISTS
 * ---------------
 * OCS list methods do not return arrays. Each wraps its rows in a differently
 * named envelope:
 *
 *   listSubscriber                 { hasMore, nbFound, subscriberList: [...] }
 *   listPrepaidPackageTemplate     { template: [...] }
 *   listDetailedLocationZone       { listDetailedLocationZone | locationZone | zone | zones: [...] }
 *   listResellerAccount            { reseller: [ { account: [...] } ] }
 *
 * Code that tests `Array.isArray(result.data)` against any of these gets false
 * and silently does nothing. That single mistake has now been found in six
 * places and cost real behaviour every time:
 *
 *   list_subscribers        status/offset/limit all inert; limit:2 returned 100 rows
 *   high_cost_subscribers   could not flag a single subscriber, ever
 *   optimize_package        usage section never rendered
 *   churn_risk              dead in BOTH branches of its usage factor
 *   marketing_intelligence  fleet distribution and catalogue sections empty
 *
 * `bounded-results.ts:105-110` already carried the zone key list; this
 * generalises it so every caller shares one reader instead of each inventing a
 * guard that happens to be wrong.
 *
 * NOT a schema validator. It answers exactly one question — "where are the
 * rows?" — and returns [] when there are none, so callers can treat an empty
 * fleet and a missing envelope the same way without a special case.
 */

/** Envelope keys per OCS method, most specific first. */
export const OCS_LIST_KEYS = {
  subscribers: ["subscriberList"],
  templates: ["template", "prepaidPackageTemplate"],
  zones: ["listDetailedLocationZone", "locationZone", "zone", "zones"],
  resellers: ["reseller"],
  packages: ["prepaidPackage", "package", "packages"],
} as const;

export type OcsListKind = keyof typeof OCS_LIST_KEYS;

/**
 * Extract the row array from an OCS response.
 *
 * Accepts a bare array unchanged, so recorded fixtures and any older OCS
 * version that really did return one keep working.
 *
 * @param data  the raw OCS response
 * @param keys  envelope keys to try, in order. Pass an OcsListKind for the
 *              known method families, or an explicit list for anything else.
 */
export function ocsArray<T = unknown>(
  data: unknown,
  keys: OcsListKind | readonly string[],
): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data === null || typeof data !== "object") return [];

  const candidates: readonly string[] =
    typeof keys === "string" ? OCS_LIST_KEYS[keys] : keys;

  const obj = data as Record<string, unknown>;
  for (const key of candidates) {
    const v = obj[key];
    if (Array.isArray(v)) return v as T[];
  }
  return [];
}

/**
 * Row count without materialising the array.
 *
 * Prefer this over `ocsArray(...).length` where only the count matters — it
 * reads the same envelope and makes the intent obvious at the call site.
 */
export function ocsCount(data: unknown, keys: OcsListKind | readonly string[]): number {
  return ocsArray(data, keys).length;
}

/**
 * Read a subscriber's ICCID from a listSubscriber row.
 *
 * WHY THIS IS NOT `row.iccid`
 * ---------------------------
 * A listSubscriber row has NO root `iccid`. Captured from the live fleet, its
 * keys are:
 *
 *   account, accountId, activationDate, allowedData, allowedMoc, allowedMosms,
 *   allowedMtc, allowedMtsms, balance, batchId, hotspotBlocked, imsiList,
 *   multiImsi, phoneNumberList, prepaid, reseller, resellerId, sim, status,
 *   subscriberId, useAccountForCharging
 *
 * The ICCID lives at `imsiList[0].iccid`. `sim` looks like the obvious home and
 * is not — it carries activationCode, esim, pin/puk and smdpServer, but no
 * ICCID.
 *
 * `String(row.iccid ?? "")` therefore yields "", and every downstream
 * per-subscriber OCS call made with that empty string returns nothing. That is
 * why high_cost_subscribers still flagged nobody after its usage-envelope bug
 * was fixed: it was asking OCS about a subscriber with no identifier.
 *
 * Returns "" when no ICCID can be found, so callers can skip a row instead of
 * calling OCS with a blank identifier.
 */
export function subscriberIccid(row: unknown): string {
  if (row === null || typeof row !== "object") return "";
  const r = row as Record<string, unknown>;

  // Canonical location.
  const imsiList = r.imsiList;
  if (Array.isArray(imsiList)) {
    for (const entry of imsiList) {
      if (entry && typeof entry === "object") {
        const v = (entry as Record<string, unknown>).iccid;
        if (typeof v === "string" && v.length > 0) return v;
        if (typeof v === "number") return String(v);
      }
    }
  }

  // Shapes seen elsewhere: getSingleSubscriber nests differently, and some
  // recorded fixtures are flattened. Accept those rather than returning "".
  for (const key of ["iccid", "ICCID"]) {
    const v = r[key];
    if (typeof v === "string" && v.length > 0) return v;
    if (typeof v === "number") return String(v);
  }
  const sim = r.sim;
  if (sim && typeof sim === "object") {
    const v = (sim as Record<string, unknown>).iccid;
    if (typeof v === "string" && v.length > 0) return v;
    if (typeof v === "number") return String(v);
  }

  return "";
}
